/* eslint-disable no-unsafe-optional-chaining */
import axios, { CancelToken } from 'axios';
import i18n from 'i18next';

import { getCache } from 'core/cache';

import get from 'lodash/get';
import throttle from 'lodash/throttle';

import { s3UploadOptions } from './getFileUploadOptions';

const getErrorMessage = (error, defaultError, handler) => {
  // don't show error on cancel
  if (error.__CANCEL__ || !handler) return;
  return handler(get(error, 'response.data.errors.message', '') || error.message || defaultError);
};

const getXmlValue = (str, key) =>
  str.substring(str.lastIndexOf(`<${key}>`) + `<${key}>`.length, str.lastIndexOf(`</${key}>`));

const onUploadProgress = throttle((progressObject, handlers) => {
  const percentageComplete = Math.floor((progressObject.loaded / progressObject.total) * 100);
  handlers?.progress(percentageComplete);
}, 1000);

export default async (file, handlers) => {
  const settings = getCache('settings');
  const isGCS =
    settings?.data?.cloud_storage_provider === 'gcs' ||
    window.assessments?.isGoogleCluster === 'true';
  const endpoint = isGCS ? 'gcs' : 'aws';
  const url = `/${endpoint}_signed_urls/new?file_name=${encodeURI(file.name)}&content_type=${
    file.type
  }&max_file_size_mb=&file_type=any`;
  const cancelToken = CancelToken.source();
  const config = {
    headers: isGCS ? { 'Content-Type': file.type } : {},
    onUploadProgress: (progressObject) => onUploadProgress(progressObject, handlers),
    cancelToken: cancelToken.token,
  };

  try {
    const { data } = await axios.get(url);
    handlers?.start(cancelToken);

    if (isGCS) {
      // GCS sends an empty response to the PUT request, so we ignore the response unless it fails.
      // The GET URL of the uploaded image is already provided by the `/gcs_signed_urls` endpoint.
      try {
        await axios.put(data.post_signed_url, file, config);

        handlers?.complete(data.get_signed_url);
        return data.get_signed_url;
      } catch (error) {
        console.log(error);
        getErrorMessage(error, i18n.t('error.upload-processing-error'), handlers?.error);
      }
    } else {
      const deserializedData = JSON.parse(data.data.replace(/\\/g, ''));

      if (!deserializedData.url) deserializedData.url = data.signed_url;
      if (!deserializedData.filename) deserializedData.filename = file.name;

      const uploadOptions = s3UploadOptions(file, deserializedData);

      try {
        const response = await axios.post(`${data.signed_url}`, uploadOptions.formData, config);

        const location = getXmlValue(response.data, 'Location');

        handlers?.complete(location);
        return location;
      } catch (error) {
        console.log(error);
        getErrorMessage(error, i18n.t('error.upload-processing-error'), handlers?.error);
      }
    }
  } catch (error) {
    console.log(error);
    getErrorMessage(error, i18n.t('error.upload-signing-error'), handlers?.error);
  }
};
